import type { HttpCancelToken, ICancelRequestToken } from '@okapi/http';
import {
  BasketCookie,
  getBasketCookie,
  mergeProducts,
  setBasketCookie,
  setBasketSetDegraded,
} from 'utils/basket';
import { isKZDomain, dispatchEvent } from 'utils/common';
import { log, pullList, pushList } from 'api/basket';
import { getCartId, isBasketSetDegraded, isUserAuth } from 'utils/cookie';
import { BASKET_EVENT_UPDATE } from './consts';
import { api } from 'api';
import { PostCartAnalyticsParams } from '../../../../api/types';

const MOSCOW_REFERENCE_STORE = '62';
const KAZAKHSTAN_REFERENCE_STORE = '113';

export class CancelError extends Error {
  name: 'CancelError'
};

export const loadBasket = async (cancelToken: HttpCancelToken) => {
  if (!isUserAuth()) {
    return getBasketCookie() ?? [];
  }

  return loadBasketRemote(cancelToken);
};

export const loadBasketRemote = async (cancelToken?: HttpCancelToken) => {
  const country = isKZDomain(location.hostname) ? 'KZ' : '';
  try {
    const data = await pullList(country, cancelToken);
    const products = data.BASKET.length ? data.BASKET[0].products : [];
    const remoteBasket = products
      .filter(({ quantity }) => quantity)
      .map(({ id, quantity }) => ({
        code: id,
        quantity,
      }));

    if (isBasketSetDegraded()) {
      const basketCookie = getBasketCookie();
      const mergedBasket = mergeProducts(basketCookie ?? [], remoteBasket);
      return mergedBasket;
    } else {
      setBasketCookie(remoteBasket);
    }

    return remoteBasket;
  } catch (error) {
    if (api.isCancel(error)) {
      throw error;
    }
    console.error(error);
    return getBasketCookie() ?? [];
  }
};

const saveBasketRemote = async (basket: BasketCookie[], cancelToken: HttpCancelToken) => {
  const isAuthorized = isUserAuth();

  if (!isAuthorized) return;

  const isKz = isKZDomain(location.hostname);
  const country = isKz ? 'KZ' : '';
  const id = getCartId() || '';

  await pushList(
    {
      list: {
        type: 'BASKET',
        id,
        ...(country && { country }),
        storeId: isKz ? KAZAKHSTAN_REFERENCE_STORE : MOSCOW_REFERENCE_STORE,
        products: basket.map(({ code, quantity }) => ({
          id: code,
          quantity,
        })),
      },
    },
    cancelToken
  );
};

const saveBasket = async (
  updatedBasket: BasketCookie[],
  cancelToken: HttpCancelToken
): Promise<BasketCookie[]> => {

  if (cancelToken.reason) {
    throw new CancelError();
  }

  setBasketCookie(updatedBasket);
    try {
      await saveBasketRemote(updatedBasket, cancelToken);
      setBasketSetDegraded(false);
    } catch (error) {
      if (api.isCancel(error)) {
        throw error;
      }
      console.error(error);
      setBasketCookie(updatedBasket);
      setBasketSetDegraded(true);
    }
  return updatedBasket;
};

const executeBasketUpdate = async (
  basketUpdateActions: BasketUpdateAction[],
  cancelToken: HttpCancelToken
): Promise<BasketCookie[]> => {
  const basket = await loadBasket(cancelToken);
  const updatedBasket = basketUpdateActions.reduce(
    (currentBasket, updateAction) => updateAction(currentBasket),
    basket
  );
  return saveBasket(updatedBasket, cancelToken);
};

export type BasketUpdateAction = (basket: BasketCookie[]) => BasketCookie[];

let basketUpdateActions: BasketUpdateAction[] = [];
let abortController: ICancelRequestToken | undefined;

const isAPIInitialized = (
  controller: ICancelRequestToken | undefined
): controller is ICancelRequestToken => {
  return controller !== undefined;
};

export const queueBasketUpdate = async (basketUpdateAction: BasketUpdateAction) => {
  basketUpdateActions.push(basketUpdateAction);
  abortController?.cancel();
  abortController = await api.abortController();
  if (!isAPIInitialized(abortController)) {
    return;
  }
  try {
    const basket = await executeBasketUpdate(basketUpdateActions, abortController.token);
    dispatchEvent(BASKET_EVENT_UPDATE, basket);
    abortController = undefined;
    basketUpdateActions = [];
  } catch (e) {
    const isCancel = api.isCancel(e) || e instanceof CancelError;
    if (!isCancel) {
      console.error('Error execute basket update: ', e);
    }
  }
};

export const logEvent = (eventName: string, detail: unknown) => {
    return log({
        description: eventName,
        data: {
            detail,
            href: location.href
        },
    });
};

export const prepareProductsForCartAnalytics = (
  products: BasketCookie[]
): PostCartAnalyticsParams => {
  return {
    products: products.map(({ code }) => code),
  };
};

export const getProductsWithIncreasedQuantity = (products: BasketCookie[]) => {
  if (isKZDomain(location.hostname)) return [];

  const currentCookie = getBasketCookie() ?? [];

  return products.filter(({ code, quantity }) => {
    const currentItem = currentCookie.find(product => product.code === code);
    const currentQuantity = currentItem ? currentItem.quantity : 0;

    return quantity > currentQuantity;
  });
};